/* eslint-disable prettier/prettier */
import { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { Label } from '../ui/Label';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { UserService } from '../../services/user.service';
import { CustomSelect } from '../ui/Select';
import { UserType } from '../../types/UserType';
import { useAuth } from '../../context/AuthContext';
import { RoleService } from '../../services/role.service';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingUser?: UserType | null;
  lockedCompanyId?: string;
  lockedRoleName?: string;
}

export const UserModal = ({
  isOpen,
  onClose,
  onSuccess,
  editingUser,
  lockedCompanyId,
  lockedRoleName,
}: Props) => {
  const { user: currentUser } = useAuth();
  const [form, setForm] = useState({
    email: '',
    roleId: '',
    roleName: '',
    companyId: '',
  });

  const [errors, setErrors] = useState({
    email: '',
    roleId: '',
    companyId: '',
  });

  const [roles, setRoles] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const isView = Boolean(editingUser);

  const handleChange = (key: string, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: '' }));

    if (key === 'roleId') {
      const role = roles.find((r) => r.value === value);
      setForm((prev) => ({ ...prev, roleName: role?.rawName || '' }));
    }
  };

  const validate = async () => {
    let valid = true;
    const newErrors = { email: '', roleId: '', companyId: '' };

    if (!form.email.trim()) {
      newErrors.email = 'Email is required.';
      valid = false;
    }

    if (!lockedRoleName && !form.roleId) {
      newErrors.roleId = 'Role is required.';
      valid = false;
    }

    const roleName = lockedRoleName ?? form.roleName;

    if (roleName === 'COMPANY_ADMIN' && !lockedCompanyId && !form.companyId) {
      newErrors.companyId = 'Company is required for this role.';
      valid = false;
    }

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = 'Invalid email format.';
      setErrors(newErrors);
      return false;
    }

    if (!valid) {
      setErrors(newErrors);
      return false;
    }

    try {
      const exists = await UserService.checkDuplicateEmail(form.email.toLowerCase());
      if (exists) {
        newErrors.email = 'Email is already in use.';
        setErrors(newErrors);
        return false;
      }
    } catch (err) {
      console.error('Email check failed:', err);
      newErrors.email = 'Unable to verify email. Please try again.';
      setErrors(newErrors);
      return false;
    }

    setErrors(newErrors);
    return true;
  };

  const handleSubmit = async () => {
    const isValid = await validate();
    if (!isValid) return;

    let resolvedRoleId = form.roleId;
    if (lockedRoleName) {
      const matchingRole = roles.find((r) => r.rawName === lockedRoleName);
      if (!matchingRole) {
        console.error('Role not found for:', lockedRoleName);
        return;
      }
      resolvedRoleId = matchingRole.value;
    }

    const payload = {
      email: form.email.toLowerCase(),
      roleId: resolvedRoleId,
      company:
        lockedCompanyId ??
        (currentUser?.role?.name === 'COMPANY_ADMIN'
          ? currentUser.company?.id
          : form.roleName === 'COMPANY_ADMIN'
            ? form.companyId
            : undefined),
    };


    try {
      await UserService.createInvitation(payload);
      onSuccess();
    } catch (err) {
      console.error('User invitation error:', err);
    }
  };

  useEffect(() => {
    if (
      currentUser?.role?.name === 'SUPER_ADMIN' &&
      !lockedCompanyId &&
      form.roleName === 'COMPANY_ADMIN' &&
      companies.length === 0
    ) {
      UserService.getCompanies().then((res) => {
        const mapped = res.data.map((c: any) => ({ label: c.name, value: c.id }));
        setCompanies(mapped);
      });
    }
  }, [form.roleName, currentUser, lockedCompanyId, companies.length]);

  useEffect(() => {
    if (isOpen) {
      RoleService.getRoles().then((res) => {
        const rolesData = res.data;

        const formatRoleName = (roleName: string) => {
          if (!roleName) return '';
          return roleName
            .trim()
            .replace(/_/g, ' ')
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
        };

        let allowedRoles: string[] = [];

        if (currentUser?.role?.name === 'SUPER_ADMIN') {
          allowedRoles = ['SUPER_ADMIN', 'COMPANY_ADMIN'];
        } else {
          allowedRoles = ['COMPANY_ADMIN', 'USER'];
        }

        const filtered = rolesData
          .filter((r: any) => allowedRoles.includes(r.name))
          .map((r: any) => ({
            label: formatRoleName(r.name),
            value: r.id,
            rawName: r.name,
          }));

        setRoles(filtered);
      });

      if (currentUser?.role?.name === 'SUPER_ADMIN') {
        UserService.getCompanies().then((res) => {
          const mapped = res.data.map((c: any) => ({ label: c.name, value: c.id }));
          setCompanies(mapped);
        });
      } else if (currentUser?.role?.name === 'COMPANY_ADMIN' && currentUser.company) {
        const mapped = [{ label: currentUser.company.name, value: currentUser.company.id }];
        setCompanies(mapped);
      }

      if (isView && editingUser) {
        setForm({
          email: editingUser.email,
          roleId: editingUser.role?.id || '',
          roleName: editingUser.role?.name || '',
          companyId: editingUser.company?.id || '',
        });
      } else {
        setForm({
          email: '',
          roleId: '',
          roleName: lockedRoleName ?? '',
          companyId: lockedCompanyId ?? '',
        });
        setErrors({ email: '', roleId: '', companyId: '' });
      }
    }
  }, [isOpen, editingUser, currentUser, lockedCompanyId, lockedRoleName]);

  if (isView && editingUser) {
    const showCompany = editingUser.role?.name !== 'SUPER_ADMIN';
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="User Details">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>First Name</Label>
            <Input value={editingUser.firstName} disabled />
          </div>
          <div>
            <Label>Last Name</Label>
            <Input value={editingUser.lastName} disabled />
          </div>
          <div>
            <Label>Username</Label>
            <Input value={editingUser.username} disabled />
          </div>
          <div>
            <Label>Email</Label>
            <Input value={editingUser.email} disabled />
          </div>
          <div>
            <Label>Country Code</Label>
            <Input value={editingUser.countryCode} disabled />
          </div>
          <div>
            <Label>Phone Number</Label>
            <Input value={editingUser.phoneNumber} disabled />
          </div>
          <div>
            <Label>Role</Label>
            <Input value={editingUser.role?.name} disabled />
          </div>
          {showCompany && (
            <div>
              <Label>Company</Label>
              <Input value={editingUser.company?.name || ''} disabled />
            </div>
          )}
        </div>
        <div className="flex justify-end mt-6">
          <Button variant="secondary" onClick={onClose} className="px-7 sm:px-10">
            Close
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Invite New User">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <Label>Email</Label>
          <Input
            value={form.email}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="user@example.com"
            error={!!errors.email}
          />
          {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
        </div>

        {!lockedRoleName ? (
          <div>
            <Label>Role</Label>
            <CustomSelect
              data={roles}
              value={form.roleId}
              onChange={(val) => handleChange('roleId', val)}
              placeholder="Select role"
              error={!!errors.roleId}
              errorMessage={errors.roleId}
            />
          </div>
        ) : (
          <div>
            <Label>Role</Label>
            <Input value={lockedRoleName} disabled />
          </div>
        )}

        {currentUser?.role?.name === 'SUPER_ADMIN' &&
          !lockedCompanyId &&
          form.roleName === 'COMPANY_ADMIN' && (
            <div>
              <Label>Company</Label>
              <CustomSelect
                data={companies}
                value={form.companyId}
                onChange={(val) => handleChange('companyId', val)}
                placeholder="Select company"
                error={!!errors.companyId}
                errorMessage={errors.companyId}
              />
            </div>
          )}

        {lockedCompanyId && currentUser?.role?.name === 'SUPER_ADMIN' && (
          <div>
            <Label>Company</Label>
            <Input
              value={companies.find((c) => c.value === lockedCompanyId)?.label || ''}
              disabled
            />
          </div>
        )}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="secondary" onClick={onClose} className="px-7 sm:px-10">
          Cancel
        </Button>
        <Button onClick={handleSubmit} className="px-7 sm:px-10">
          Invite
        </Button>
      </div>
    </Modal>
  );
};
